import type { MouseEvent } from 'react';
import { INTERACTIVE_ELEMENT_SELECTOR } from '@zg-rentals/satellite-components';

export type PointerDownPosition = {
  clientX: number;
  clientY: number;
};

const DRAG_THRESHOLD_PX = 5;

/**
 * True when a click on a clickable row belongs to something other than the row: a nested control,
 * portaled tooltip or menu content (React bubbles those through the component tree, not the DOM),
 * the end of a drag, or the end of a text selection inside the row.
 */
export default function shouldIgnoreRowClick(
  event: MouseEvent<HTMLElement>,
  pointerDown: PointerDownPosition | null,
): boolean {
  const { currentTarget, target } = event;

  if (!(target instanceof Element) || !currentTarget.contains(target)) {
    return true;
  }

  if (target.closest(INTERACTIVE_ELEMENT_SELECTOR)) {
    return true;
  }

  // A drag within the row still fires a click, and its user-select: none cells leave no selection to catch.
  if (
    pointerDown &&
    Math.hypot(event.clientX - pointerDown.clientX, event.clientY - pointerDown.clientY) > DRAG_THRESHOLD_PX
  ) {
    return true;
  }

  const selection = window.getSelection();
  return !!selection?.toString() && currentTarget.contains(selection.anchorNode);
}
